/**
 * The UI registry: a window or HUD piece that needs no wiring beyond the World, `send` and a toast mounts itself from a
 * file of its own, `ui/<name>.ui.ts`, which calls `registerUi` when it loads (ui/uis.ts loads them all). main.ts mounts
 * them once the HUD exists, hands them every World event, syncs them every frame and asks them first about Esc.
 */
import type { PlayerCommand, World, WorldEvent } from '@nine/shared';

export interface UiContext {
  readonly world: World;
  send(c: PlayerCommand): void;
  toast(text: string): void;
}

export interface UiPart {
  handle?(ev: WorldEvent): void;
  sync?(dt: number): void;
  /** Esc: true when this part closed something. */
  escape?(): boolean;
}

const makers: { id: string; mount: (ctx: UiContext) => UiPart }[] = [];

export function registerUi(id: string, mount: (ctx: UiContext) => UiPart): void {
  if (makers.some((m) => m.id === id)) throw new Error(`ui ${id} registered twice`);
  makers.push({ id, mount });
}

/** Mounts every registered part, in id order (stable across loads). */
export function mountRegisteredUis(ctx: UiContext): UiPart[] {
  return [...makers].sort((a, b) => a.id.localeCompare(b.id)).map((m) => m.mount(ctx));
}
